import React, { useEffect, useState } from "react";
import { mxcToHttp } from "../../utils";
import { FileOverlay, FileModalContent,
    Title, CsvTable,
    TextPre, PdfWrapper,
    ModalCloseButtonStyled, CenteredLoader
} from "src/scenes/Chat/components/styled/chatAttachmentStyled";
import { LottieLoadingIndicator } from "src/components";

export const MediaPreviewModal = ({ isOpen, onClose, url, type, fileName, baseUrl, thumbnailUrl = null, videoUrl }) => {
    const [textContent, setTextContent] = useState(null);
    const [csvRows, setCsvRows] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [videoLoading, setVideoLoading] = useState(true);

    const safeFileName = (() => {
        if (!fileName) return "unknown_file";
        if (typeof fileName === "string") return fileName;
        if (fileName instanceof File && fileName.name) return fileName.name;
        if (fileName.file && fileName.file.name) return fileName.file.name;
        if (fileName.body && typeof fileName.body === "string") return fileName.body;
        return "unknown_file";
    })();

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") onClose();
        };
        if (isOpen) document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen || !url) {
            setTextContent(null);
            setCsvRows(null);
            setPdfUrl(null);
            return;
        }

        (async () => {
            const lower = safeFileName.toLowerCase();
            // will add other documents type if needed like (pptx, docx, etc)
            try {
                if (lower.endsWith(".csv")) {
                    const text = await fetch(url).then((r) => r.text());
                    setCsvRows(text.split(/\r?\n/).map((row) => row.split(",")));
                    setTextContent(null);
                    setPdfUrl(null);
                } else if (
                    lower.endsWith(".txt") ||
                    lower.endsWith(".json") ||
                    lower.endsWith(".md")
                ) {
                    const text = await fetch(url).then((r) => r.text());
                    setTextContent(text);
                    setCsvRows(null);
                    setPdfUrl(null);
                } else if (lower.endsWith(".pdf")) {
                    let finalUrl = url;
                    if (url.startsWith("mxc://") && baseUrl) {
                        finalUrl = mxcToHttp(url, baseUrl);
                    }

                    const response = await fetch(finalUrl);
                    const arrayBuffer = await response.arrayBuffer();
                    const pdfBlob = new Blob([arrayBuffer], { type: "application/pdf" });
                    const objectUrl = URL.createObjectURL(pdfBlob);

                    setPdfUrl(objectUrl);
                    setTextContent(null);
                    setCsvRows(null);
                } else {
                    setTextContent(null);
                    setCsvRows(null);
                    setPdfUrl(null);
                }
            } catch {
                setTextContent(null);
                setCsvRows(null);
                setPdfUrl(null);
            }
        })();
    }, [isOpen, url, safeFileName]);

    if (!isOpen || !url) return null;

    const lower = safeFileName.toLowerCase();
    const isPdf = lower.endsWith(".pdf");
    const isCsv = lower.endsWith(".csv");
    const isText = lower.endsWith(".txt") || lower.endsWith(".json") || lower.endsWith(".md");

    if (type === "m.image") {
        return (
            <FileOverlay onClick={onClose}>
                <div style={{ position: "relative" }}>
                    <ModalCloseButton onClose={onClose} />
                    <img src={url} alt="preview"
                        style={{
                            maxHeight: "80vh",
                            maxWidth: "90vw",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </FileOverlay>
        );
    }

    if (type === "m.video") {
        return (
            <FileOverlay onClick={onClose}>
                <div style={{ position: "relative" }}>
                    <ModalCloseButton onClose={onClose} />
                    {/* Centered loader overlay */}
                    {videoLoading && (
                        <CenteredLoader>
                            <LottieLoadingIndicator height="60px" width="60px" />
                        </CenteredLoader>
                    )}
                    <video
                        src={videoUrl}
                        controls
                        autoPlay
                        playsInline
                        onLoadedData={() => setVideoLoading(false)}
                        poster={thumbnailUrl || undefined}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxHeight: "80vh",
                            maxWidth: "90vw",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto"
                        }}
                    />
                </div>
            </FileOverlay>
        );
    }
    return (
        <FileOverlay onClick={onClose}>
            <FileModalContent onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
                {/* Close Button */}
                <ModalCloseButton onClose={onClose} />

                <Title>{safeFileName}</Title>

                {isPdf && pdfUrl && (
                    <PdfWrapper>
                        <iframe src={pdfUrl} title="PDF Preview" width="100%" height="100%" style={{ border: "none" }} />
                    </PdfWrapper>
                )}

                {!isPdf && isCsv && csvRows && (
                    <div style={{ maxHeight: "70vh", overflow: "auto" }}>
                        <CsvTable>
                            <tbody>
                                {csvRows.map((cols, i) => (
                                    <tr key={i}>
                                        {cols.map((cell, j) => (
                                            <td key={j}>{cell}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </CsvTable>
                    </div>
                )}

                {!isPdf && isText && textContent && <TextPre>{textContent}</TextPre>}

                {!isPdf && !isCsv && !isText && (
                    <div style={{ padding: "2rem", textAlign: "center", color: "#333" }}>
                        <p>This type of file cannot be previewed. Please download it to view.</p>
                    </div>
                )}
            </FileModalContent>
        </FileOverlay>
    );
};

const ModalCloseButton = ({ onClose }) => {
    return <ModalCloseButtonStyled onClick={onClose} />;
};